"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface AuthShellProps {
  readonly title: string;
  readonly subtitle: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

export function AuthShell({ title, subtitle, children, footer }: AuthShellProps) {
  const pathname = usePathname();
  const isLogin = pathname === "/login";
  const isCadastro = pathname === "/cadastro";

  return (
    <div className="relative min-h-screen overflow-hidden bg-brand-dark">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(124,58,237,0.25),_transparent_55%),_radial-gradient(circle_at_bottom_right,_rgba(34,197,94,0.15),_transparent_45%)]" />

      <div className="relative z-10 mx-auto flex min-h-screen w-full items-center justify-center px-4 py-8">
        {/* Modal Container - Mais compacto */}
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d14]/95 p-6 shadow-2xl backdrop-blur-xl sm:p-8">
          
          {/* Logo Clicável */}
          <div className="mb-5 flex justify-center">
            <Link 
              href="/" 
              className="inline-block transition-transform hover:scale-105"
              title="Ir para página inicial"
            >
              <Image
                src="/images/logo/logo.png"
                alt="Artes Turbo"
                width={160}
                height={40}
                priority
                className="h-auto w-32 sm:w-40"
              />
            </Link>
          </div>

          {/* Tabs Entrar/Cadastrar */}
          <div className="mb-6 flex justify-center">
            <div className="inline-flex rounded-full bg-white/5 p-1">
              <Link
                href="/login"
                className={`rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200 ${
                  isLogin
                    ? "bg-brand-gradient text-white shadow-md"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Entrar
              </Link>
              <Link
                href="/cadastro"
                className={`rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200 ${
                  isCadastro
                    ? "bg-brand-gradient text-white shadow-md"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Cadastrar
              </Link>
            </div>
          </div>

          {/* Título e Subtítulo */}
          <div className="mb-5 text-center">
            <h1 className="text-xl font-bold text-white sm:text-2xl">{title}</h1>
            <p className="mt-1.5 text-sm text-slate-400">{subtitle}</p>
          </div>

          {/* Formulário */}
          <div className="space-y-4">
            {children}
          </div>

          {/* Footer opcional */}
          {footer ? (
            <div className="mt-6 border-t border-white/10 pt-5">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
